import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_LINE, AXIS_TICK, ChartCard, ChartEmpty, GRID, SeriesToggle, readToggle, storeToggle } from '@/pages/GamAdvertisers/components/charts';
import type { CacBasis, LLCurve } from '@/types/lead-lifetime';
import { useMoney } from './currency';
import { BASIS_LABEL, cacKey, fmtLL, ltvKey } from './format';

const LTV_COLOR = '#10b981';
const CAC_COLOR = '#ef4444';

function CurveTooltip({ active, payload }: any) {
    const money = useMoney();
    if (!active || !payload?.length) return null;
    const p = payload[0].payload as { day_n: number; ltv: number | null; cac: number | null; roi: number | null; cohorts: number };
    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 text-white shadow-xl">
            <p className="mb-1 text-xs font-semibold">D{p.day_n} depois da entrada</p>
            <p className="text-xs"><span style={{ color: LTV_COLOR }}>●</span> LTV acumulado: <b className="tabular-nums">{money.unit(p.ltv)}</b></p>
            <p className="text-xs"><span style={{ color: CAC_COLOR }}>●</span> CAC: <b className="tabular-nums">{money.unit(p.cac)}</b></p>
            <p className="text-xs">ROI no dia: <b className="tabular-nums">{fmtLL.pct(p.roi)}</b></p>
            <p className="mt-1 text-[10px] opacity-70">{p.cohorts} coorte(s) com esse dia completo</p>
        </div>
    );
}

/**
 * Curva CAC × LTV de D0 a D90. No ponto N entram só as coortes que já completaram N dias
 * (e o CAC é dessas mesmas coortes), por isso a linha de CAC pode oscilar um pouco.
 */
export function CurveChart({ curve, basis }: { curve: LLCurve; basis: CacBasis }) {
    const money = useMoney();
    const [showLtv, setShowLtv] = useState(() => readToggle('lltv_curve_ltv', true));
    const [showCac, setShowCac] = useState(() => readToggle('lltv_curve_cac', true));

    const data = useMemo(
        () => curve.points
            .filter((p) => p.cohorts > 0)
            .map((p) => ({ day_n: p.day_n, ltv: p[ltvKey(basis)], cac: p[cacKey(basis)], roi: p.roi, cohorts: p.cohorts })),
        [curve.points, basis]
    );
    const payback = curve.payback_day;
    const subtitle = payback == null
        ? `Por ${BASIS_LABEL[basis]} · ainda não se pagou no período`
        : `Por ${BASIS_LABEL[basis]} · se paga no D${payback}`;

    return (
        <ChartCard
            title="CAC × LTV por dia desde a entrada"
            subtitle={`${subtitle} · cada ponto usa só as coortes que já completaram aquele dia`}
            right={
                <div className="flex gap-1.5">
                    <SeriesToggle label="LTV" color={LTV_COLOR} active={showLtv} onChange={(v) => { setShowLtv(v); storeToggle('lltv_curve_ltv', v); }} hint="Receita líquida acumulada por lead desde o dia da entrada" />
                    <SeriesToggle label="CAC" color={CAC_COLOR} active={showCac} onChange={(v) => { setShowCac(v); storeToggle('lltv_curve_cac', v); }} hint="Gasto com anúncio dividido pela base escolhida, das mesmas coortes" />
                </div>
            }
        >
            {data.length === 0 ? (
                <ChartEmpty height={300} />
            ) : (
                <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                            <CartesianGrid {...GRID} vertical={false} />
                            <XAxis dataKey="day_n" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} tickFormatter={(v) => `D${v}`} />
                            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => money.fmt(Number(v))} />
                            <Tooltip content={<CurveTooltip />} />
                            {payback != null && (
                                <ReferenceLine x={payback} stroke="#64748b" strokeDasharray="4 4" label={{ value: `payback D${payback}`, fontSize: 10, fill: 'currentColor', position: 'top' }} />
                            )}
                            {showCac && <Line type="monotone" dataKey="cac" name="CAC" stroke={CAC_COLOR} strokeWidth={2} dot={false} connectNulls />}
                            {showLtv && <Line type="monotone" dataKey="ltv" name="LTV" stroke={LTV_COLOR} strokeWidth={2.5} dot={false} connectNulls />}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </ChartCard>
    );
}
